'use client';

import * as React from 'react';
import Image from 'next/image';
import { MediaItem } from '@/models/media-item';
import { getMediaThumbnail, isDriveVideo } from '@/lib/drive-thumbnails';
import { getVideoThumbnailUrl } from '@/models/media-item';
import { Play, Loader2, Video } from 'lucide-react';

interface DriveThumbnailProps {
  media: MediaItem;
  alt: string;
  width?: number;
  height?: number;
  fill?: boolean;
  className?: string;
  showPlayIcon?: boolean;
}

/**
 * Component that handles async thumbnail loading for Google Drive videos
 * Falls back to regular thumbnail handling for non-Drive videos
 */
export function DriveThumbnail({
  media,
  alt,
  width,
  height,
  fill,
  className,
  showPlayIcon = true
}: DriveThumbnailProps) {
  const [thumbnailUrl, setThumbnailUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [showPlaceholder, setShowPlaceholder] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;

    async function loadThumbnail() {
      // For non-video media, use standard thumbnail
      if (media.type !== 'video') {
        setThumbnailUrl(media.thumbnailUrl);
        return;
      }

      // For Drive videos, always use our proxy API
      if (isDriveVideo(media)) {
        setLoading(true);
        setShowPlaceholder(false);

        try {
          const url = await getMediaThumbnail(media);
          if (mounted) {
            setThumbnailUrl(url);
            setLoading(false);
          }
        } catch (err) {
          console.error('Error loading Drive thumbnail:', err);
          if (mounted) {
            setShowPlaceholder(true);
            setLoading(false);
          }
        }
        return;
      }

      // For non-Drive videos, try standard method
      const standardThumbnail = getVideoThumbnailUrl(media);
      if (standardThumbnail) {
        setThumbnailUrl(standardThumbnail);
        return;
      }

      // No thumbnail available
      setShowPlaceholder(true);
    }

    loadThumbnail();

    return () => {
      mounted = false;
    };
  }, [media]);

  // Video placeholder component
  const VideoPlaceholder = () => (
    <div className={`bg-gradient-to-br from-gray-700 to-gray-900 flex flex-col items-center justify-center gap-2 ${className || ''}`}>
      <Video className="w-16 h-16 text-gray-400" strokeWidth={1.5} />
      <div className="bg-black/30 rounded-full p-2">
        <Play className="w-8 h-8 text-white" fill="white" />
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className={`bg-muted flex items-center justify-center ${className || ''}`}>
        <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
      </div>
    );
  }

  // Show placeholder if explicitly set
  if (showPlaceholder || !thumbnailUrl) {
    return <VideoPlaceholder />;
  }

  // Render thumbnail with optional play icon and error fallback
  return (
    <div className="relative w-full h-full">
      <Image
        src={thumbnailUrl}
        alt={alt}
        width={width}
        height={height}
        fill={fill}
        className={className}
        onError={() => {
          // Fallback to placeholder if image fails to load
          setShowPlaceholder(true);
        }}
      />
      {showPlayIcon && media.type === 'video' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-black/50 rounded-full p-3">
            <Play className="w-12 h-12 text-white drop-shadow-lg" />
          </div>
        </div>
      )}
    </div>
  );
}
